import { useLazyQuery, useMutation } from '@apollo/client';
import { useIsAuthenticated } from '@azure/msal-react';
import { Button, Card, DatePicker, Form, Row, Select, notification, Descriptions } from 'antd';
import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  GET_PROCEEDING_STATUS_HISTORY,
  UPDATE_PROCEEDING_STATUS,
  GET_PROCEEDING_DETAILS,
} from './ProceedingStatusQueries';
import {
  cancellationReasonOptions,
  statusOptions,
  decisionOutcomeOptions,
} from './ProceedingStatusConstants';
import CustomInput from '../CustomComponents/CustomInput';

const ProceedingStatusForm = () => {
  const { proceedingId } = useParams<any>();
  const [form] = Form.useForm();
  const today = new Intl.DateTimeFormat('en-ca', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  const isAuthenticated = useIsAuthenticated();
  const [reasonLabel, setReasonLabel] = useState(null);
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const [showReasonLabel, setShowReasonLabel] = useState(false);
  const [cancellationOtherReason, setCancellationOtherReason] = useState(false);
  const [createdDate, setCreatedDate] = useState(null);
  const [statusOptionList, setStatusOptionList] = useState(statusOptions);

  const [showDecisionFields, setShowDecisionFields] = useState(false);
  const [showDecisionNumberForCancelled, setShowDecisionNumberForCancelled] = useState(false);
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [otherOutcome, setOtherOutcome] = useState('');


  const toggle = (value: string) => {
    if (value === 'Adjourned') {
      setShowReasonLabel(true);
      setCancellationOtherReason(false);
      setReasonLabel('Adjournment Reason');
      setShowDecisionFields(false);
      setShowDecisionNumberForCancelled(false);
    } else if (value === 'Cancelled') {
      setShowReasonLabel(true);
      setCancellationOtherReason(false);
      setReasonLabel('Cancellation Reason');
      setShowDecisionFields(false);
      setShowDecisionNumberForCancelled(true);
    } else if (value === 'Completed') {
      setShowReasonLabel(false);
      setCancellationOtherReason(false);
      setShowDecisionFields(true);
      setShowDecisionNumberForCancelled(false);
    } else {
      setShowReasonLabel(false);
      setCancellationOtherReason(false);
      setShowDecisionFields(false);
      setShowDecisionNumberForCancelled(false);
    }
  };

  const handleDecisionOutcomeChange = (value: string) => {
    if (value === 'Other') {
      setIsOtherSelected(true);
    } else {
      setIsOtherSelected(false);
      setOtherOutcome('');
    }
  };


  const toggleCancellationOther = (value: string) => {
    if (value !== 'Other') {
      setCancellationOtherReason(false);
      return;
    } else {
      setCancellationOtherReason(true);
    }
  };

  const [getProceedingLatestStatusHistory, { data: data, refetch }] = useLazyQuery(
    GET_PROCEEDING_STATUS_HISTORY
  );

  const [getProceedingDetail, { data: proceedingData }] = useLazyQuery(GET_PROCEEDING_DETAILS);

  const [updateProceedingStatus, { loading: updating, data: updateResult, error: updateError }] =
    useMutation(UPDATE_PROCEEDING_STATUS);

  useEffect(() => {
    if (tab === '7' && proceedingId) {
      getProceedingLatestStatusHistory({ variables: { input: proceedingId } });
      getProceedingDetail({ variables: { input: proceedingId } });
    }
  }, [tab]);

  useEffect(() => {
    form.resetFields();
    setStatusOptionList(
      statusOptions.filter(
        (status) =>
          status.value !==
          (data?.proceedingStatusHistory?.status ?? proceedingData?.proceeding.status)
      )
    );
    setCreatedDate(proceedingData?.proceeding.createdDate);
  }, [data?.proceedingStatusHistory, proceedingData]);

  const onFinish = async (values: any) => {
    try {
      if (isAuthenticated) {
        await updateProceedingStatus({
          variables: {
            input: {
              proceedingNumber: proceedingId,
              status: values.newStatus,
              reason: values.otherCancellationReason ?? values.reason,
              statusEffectiveDate: values.newStatusDate,
              decisionDate: values.decisionDate,
              decisionNumber: values.decisionNumber,
              decisionOutcome: values.decisionOutcome,
              otherOutcome: values.otherOutcome,
              panelCondition: values.panelCondition,
            },
          },
        });
        refetch();
        setShowReasonLabel(false);
        setCancellationOtherReason(false);
        setReasonLabel(null);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (
      isAuthenticated &&
      updateResult !== undefined &&
      updateResult.updateProceedingStatus.result === 'Success'
    ) {
      notification.success({
        message: 'Success',
        description: `Proceeding status has been updated successfully.`,
      });
    }
    if (
      isAuthenticated &&
      updateResult !== undefined &&
      updateResult.updateProceedingStatus.result === 'Error'
    ) {
      notification.error({
        message: 'Error',
        description: `An error occurred in updating the proceeding status.`,
      });
    }
  }, [updateResult]);

  return (
    <>
      <div style={{ margin: '0 20px', minHeight: '100vh', marginTop: '10px' }}>
        <Row>
          <Card
            title={<span className="highlighted-title">Proceeding Status</span>}
            style={{ marginTop: '35px', width: '900px' }}
            className="card"
          >
            <Row style={{ marginBottom: '30px' }}>
              <Descriptions>
                <Descriptions.Item label="Current Status">
                  {data?.proceedingStatusHistory?.status ?? proceedingData?.proceeding.status}
                </Descriptions.Item>
                <Descriptions.Item label="Status Reason">
                  {data?.proceedingStatusHistory?.reason ?? 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Status Effective Date">
                  {data?.proceedingStatusHistory?.statusEffectiveDate ?? createdDate}
                </Descriptions.Item>
                <Descriptions.Item label="Decision Date">
                  {data?.proceedingStatusHistory?.decisionDate ?? 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Decision Number">
                  {data?.proceedingStatusHistory?.decisionNumber ?? 'N/A'}
                </Descriptions.Item>

                {data?.proceedingStatusHistory?.decisionOutcome === 'Other' ? (
                  <>
                    <Descriptions.Item label="Decision Outcome">Other</Descriptions.Item>
                    <Descriptions.Item label="Decision Other Outcome">
                      {data?.proceedingStatusHistory?.otherOutcome ?? 'N/A'}
                    </Descriptions.Item>
                  </>
                ) : (
                  <Descriptions.Item label="Decision Outcome">
                    {data?.proceedingStatusHistory?.decisionOutcome ?? 'N/A'}
                  </Descriptions.Item>
                )}

                <Descriptions.Item label="Panel Condition">
                  {data?.proceedingStatusHistory?.panelCondition ?? 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            </Row>
            <Form form={form} layout="vertical" onFinish={onFinish}>
              {isAuthenticated && (
                <Form.Item
                  name="newStatus"
                  label="New Status"
                  style={{ width: '100%' }}
                  rules={[{ required: true, message: 'Please provide a status.' }]}
                >
                  <Select options={statusOptionList} onChange={toggle} />
                </Form.Item>
              )}
              {isAuthenticated && (
                <Form.Item
                  name="newStatusDate"
                  label="New Status Date"
                  style={{ width: '100%' }}
                  rules={[{ required: true, message: 'Please select a date.' }]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                  ></DatePicker>
                </Form.Item>
              )}
              {isAuthenticated && showDecisionFields && (
                <>
                  <Form.Item name="decisionDate" label="Decision Date" style={{ width: '100%' }}>
                    <DatePicker
                      style={{ width: '100%' }}
                      disabledDate={(current) => current.isAfter(today)}
                    />
                  </Form.Item>
                  <Form.Item
                    name="decisionNumber"
                    label="Decision Number"
                    style={{ width: '100%' }}
                  >
                    <CustomInput showInput={true}></CustomInput>
                  </Form.Item>
                  
                  <Form.Item
                    name="decisionOutcome"
                    label="Decision Outcome"
                    style={{ width: '100%' }}
                    rules={[{ required: true, message: 'Please select a decision outcome.' }]}
                  >
                    <Select
                      onChange={handleDecisionOutcomeChange}
                      options={decisionOutcomeOptions}
                    />
                  </Form.Item>

                  {isOtherSelected && (
                    <Form.Item
                      name="otherOutcome"
                      label="Please specify the other outcome"
                      style={{ width: '100%' }}
                      rules={[{ required: true, message: 'Please specify the outcome.' }]}
                    >
                      <CustomInput
                        value={otherOutcome}
                        onChange={(e) => setOtherOutcome(e.target.value)}
                        showInput={true}
                      />
                    </Form.Item>
                  )}

                  <Form.Item
                    name="panelCondition"
                    label="Panel Conditions"
                    style={{ width: '100%' }}
                  >
                    <CustomInput showInput={true}></CustomInput>
                  </Form.Item>
                </>
              )}

              {showDecisionNumberForCancelled && (
                <>
                  <Form.Item
                    name="decisionNumber"
                    label="Decision Number"
                    style={{ width: '100%' }}
                  >
                    <CustomInput showInput={true} disabled={!isAuthenticated}></CustomInput>
                  </Form.Item>
                </>
              )}
              {showReasonLabel && (
                <Form.Item
                  name="reason"
                  label={reasonLabel}
                  style={{ width: '100%' }}
                  rules={[{ required: true, message: 'Please provide a reason.' }]}
                >
                  {reasonLabel === 'Adjournment Reason' && (
                    <CustomInput showInput={true} disabled={!isAuthenticated}></CustomInput>
                  )}
                  {reasonLabel === 'Cancellation Reason' && (
                    <Select
                      options={cancellationReasonOptions}
                      onChange={toggleCancellationOther}
                    />
                  )}
                </Form.Item>
              )}
              {showReasonLabel && cancellationOtherReason && (
                <Form.Item
                  name="otherCancellationReason"
                  label="Other Cancellation Reason"
                  style={{ width: '100%' }}
                  rules={[{ required: true, message: 'Please provide a reason.' }]}
                >
                  <CustomInput showInput={true} disabled={!isAuthenticated}></CustomInput>
                </Form.Item>
              )}
              {isAuthenticated && (
                <Form.Item>
                  <Button type="primary" htmlType="submit">
                    Submit
                  </Button>
                </Form.Item>
              )}
            </Form>
          </Card>
        </Row>
      </div>
    </>
  );
};

export default ProceedingStatusForm;
